import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import Contact from '../Landing/Contact';

function HomePage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const section = document.querySelector(location.hash);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  return (
    <div className="bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 px-4 md:px-8 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6">
              Stop Searching. <span className="bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent">Start Executing.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-2 font-semibold">
              Operational AI for the Clinical Research Industry
            </p>
            <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto mt-6">
              Kenosha AI delivers secure, enterprise-grade AI tools designed for the unique needs of CROs and biopharma companies. With ProChat, RegChat, and Operational Agents, your teams get instant, source-linked answers from SOPs, study documents, and regulatory guidance—along with automation of critical workflows not covered in CTMS or EDC systems—so your teams spend less time digging and more time delivering.
            </p>
          </div>
          <div className="flex justify-center mt-8">
            <Link to="/#contact">
              <button className="bg-[#191C85] hover:bg-[#191C85]/90 text-white font-bold py-4 px-8 rounded-xl text-lg transition duration-300">
                See Kenosha AI in action – Request a demo
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Kenosha AI Section */}
      <section className="py-16 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-8 text-center">
            Why Kenosha AI
          </h2>
          <p className="text-lg text-gray-600 mb-8 text-center max-w-4xl mx-auto">
            Clinical research is overloaded with SOPs, study specific documents, regulatory guidance, and scattered systems. CTMS and EDC platforms don't cover the everyday knowledge and workflow gaps that slow down study execution.
          </p>
          <p className="text-lg text-gray-700 mb-6 font-semibold text-center">
            Kenosha AI fills the gap with AI agents that:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">📄</div>
              <p className="text-gray-700">Deliver instant answers from SOPs, study documents, and global regulatory guidance.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">🔗</div>
              <p className="text-gray-700">Provide page-level citations and links to source documents and authoritative sources.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">⚙️</div>
              <p className="text-gray-700">Automate SOP-driven workflow tasks still handled manually in spreadsheets and inboxes.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
              <div className="text-3xl mb-3">🔒</div>
              <p className="text-gray-700">Ensure enterprise-grade privacy and security on Microsoft Azure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ProChat Section */}
      <section className="py-16 px-4 md:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="md:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-6">
                ProChat™
              </h2>
              <h3 className="text-xl md:text-2xl font-semibold text-gray-700 mb-4">
                Turn SOPs and study folders into an AI knowledge hub.
              </h3>
              <p className="text-gray-600 mb-6">
                ProChat connects directly to your SOPs and SharePoint study folders, enabling your team to ask natural-language questions and get instant answers with links to the policies, processes, and documents they need every day.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span className="text-gray-700"><strong>General SOP questions</strong> → fast, consistent compliance answers.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span className="text-gray-700"><strong>Study-specific information</strong> → immediate answers and access to the right version of the right documents.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span className="text-gray-700"><strong>RFI/RFP responses</strong> → generate precise, SOP-based responses in the required format and word counts, saving hours of resource time on each RFP.</span>
                </li>
              </ul>
              <Link to="/prochat">
                <button className="bg-[#191C85] hover:bg-[#191C85]/90 text-white font-semibold py-3 px-6 rounded-xl transition duration-300">
                  Learn more about ProChat →
                </button>
              </Link>
            </div>
            <div className="md:w-1/2">
              <div className="bg-gradient-to-br from-blue-100 to-teal-50 rounded-2xl p-8 shadow-lg">
                <div className="bg-white rounded-xl p-6">
                  <div className="text-6xl text-center mb-4">💬</div>
                  <p className="text-center text-gray-700 font-semibold">AI-Powered Process Intelligence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RegChat Section */}
      <section className="py-16 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row-reverse gap-12 items-center">
            <div className="md:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-6">
                RegChat™
              </h2>
              <h3 className="text-xl md:text-2xl font-semibold text-gray-700 mb-4">
                Global regulatory intelligence at your fingertips.
              </h3>
              <p className="text-gray-600 mb-6">
                RegChat connects you to official guidance from 135+ countries and major agencies (ICH, FDA, EMA, MHRA, PMDA, and more). You can think of RegChat as a private and secure ChatGPT that is dedicated to international clinical regulatory guidance.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span className="text-gray-700"><strong>Multi-agency queries</strong> answered with citations and direct links to sources.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span className="text-gray-700"><strong>Comparisons</strong> of country requirements for ethics standards, submissions, and manufacturing.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span className="text-gray-700"><strong>Data management guidance</strong> and best practices to ensure quality and compliance.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">✓</span>
                  <span className="text-gray-700"><strong>ClinicalTrials.gov Assistant</strong> → search 500,000+ trial records by indications, endpoints, biomarkers, and I/E criteria for streamlined planning and submissions.</span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/regchat">
                  <button className="bg-[#191C85] hover:bg-[#191C85]/90 text-white font-semibold py-3 px-6 rounded-xl transition duration-300">
                    Learn more about RegChat →
                  </button>
                </Link>
                <a href="https://RegChat.com" target="_blank" rel="noopener noreferrer">
                  <button className="bg-transparent border-2 border-[#191C85] hover:bg-[#191C85] text-[#191C85] hover:text-white font-semibold py-3 px-6 rounded-xl transition duration-300">
                    Start free trial →
                  </button>
                </a>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="bg-gradient-to-br from-teal-100 to-blue-50 rounded-2xl p-8 shadow-lg">
                <div className="bg-white rounded-xl p-6">
                  <div className="text-6xl text-center mb-4">🌍</div>
                  <p className="text-center text-gray-700 font-semibold">135+ Countries & Agencies</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Operational Agents Section */}
      <section className="py-16 px-4 md:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#191C85] mb-6 text-center">
            Operational Agents for Custom Workflow Tasks
          </h2>
          <p className="text-lg text-gray-600 mb-8 text-center max-w-4xl mx-auto">
            Where spreadsheets and emails fall short, AI takes over. Too many essential study tasks—protocol deviations, issue escalation logs, training compliance, enrollment & screen failure logs, delegation logs, and site communications—remain stuck in spreadsheets and inboxes, creating delays, inefficiency, and compliance risks.
          </p>
          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl p-8 max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 text-center">
              Kenosha AI's Operational Agents bring <strong>automation and intelligence</strong> to these SOP-driven workflows, ensuring <strong>audit-ready traceability, accuracy, and speed</strong> across your study operations.
            </p>
          </div>
        </div>
      </section>

      {/* Closing CTA Section */}
      <section className="py-16 px-4 md:px-8 bg-[#191C85]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Operational AI for Clinical Research
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Empower your teams with instant answers, regulatory clarity, and workflow automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#contact">
              <button className="bg-white hover:bg-gray-100 text-[#191C85] font-bold py-4 px-8 rounded-xl text-lg transition duration-300">
                Request a Demo
              </button>
            </Link>
            <a href="https://RegChat.com" target="_blank" rel="noopener noreferrer">
              <button className="bg-transparent border-2 border-white hover:bg-white text-white hover:text-[#191C85] font-bold py-4 px-8 rounded-xl text-lg transition duration-300">
                Start Free RegChat Trial
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <Contact />

      <Footer />
    </div>
  );
}

export default HomePage;